import { Outlet, NavLink, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_attorney-ready/artifacts/06pkbseh_lexintake%20logo.png";

const navLinks = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/how-it-works", label: "How It Works", testId: "nav-how-it-works" },
  { to: "/attorneys", label: "Attorneys & Law Firms", testId: "nav-attorneys" },
  { to: "/about", label: "About", testId: "nav-about" },
];

export default function MarketingLayout() {
  const { user } = useAuth();
  const dashHref =
    user?.role === "admin"
      ? "/admin/dashboard"
      : user?.role === "attorney" || user?.role === "law_firm"
        ? user?.status === "approved"
          ? user?.subscription_status === "active" || user?.subscription_status === "trialing"
            ? "/attorney/dashboard"
            : "/billing"
          : "/pending"
        : null;
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 group"
          >
            <img
              src={LOGO_URL}
              alt="LexIntake"
              className="h-16 w-auto object-contain"
              data-testid="logo-img"
            />
            <span className="sr-only">LexIntake</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive
                      ? "text-[#0A192F]"
                      : "text-slate-600 hover:text-[#0A192F]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <Link to={dashHref || "/"} data-testid="header-dashboard-link">
                <Button
                  className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-5"
                  data-testid="header-dashboard-btn"
                >
                  Go to dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-link">
                  <Button
                    variant="ghost"
                    className="text-slate-700 hover:text-[#0A192F]"
                    data-testid="header-login-btn"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/intake" data-testid="header-start-link">
                  <Button
                    className="bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg px-5"
                    data-testid="header-start-case-btn"
                  >
                    Start Free Case Review
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer
        data-testid="site-footer"
        className="border-t border-slate-200 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <img
                src={LOGO_URL}
                alt="LexIntake"
                className="h-20 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-slate-500 max-w-md leading-relaxed">
              Tell us what happened. Lexi AI organizes your legal situation
              into an attorney-ready case file.
            </p>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-3">
              Platform
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>
                <Link to="/how-it-works" className="hover:text-[#0A192F]">
                  How It Works
                </Link>
              </li>
              <li>
                <Link to="/attorneys" className="hover:text-[#0A192F]">
                  For Attorneys
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#0A192F]">
                  About
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500 mb-3">
              Get Started
            </p>
            <ul className="space-y-2 text-sm text-slate-600">
              <li>
                <Link to="/intake" className="hover:text-[#0A192F]">
                  Start Free Case Review
                </Link>
              </li>
              <li>
                <Link to="/login" className="hover:text-[#0A192F]">
                  Attorney Login
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 text-xs text-slate-500 leading-relaxed">
            <p data-testid="footer-disclaimer">
              <strong className="text-slate-700">Important:</strong> LexIntake
              is not a law firm and does not provide legal advice. No
              attorney-client relationship is formed by using this service.
              Information you submit is used for legal intake and case
              organization purposes. Please consult a licensed attorney for
              legal advice. © {new Date().getFullYear()} LexIntake.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
